import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import type { AdminSeriesPoint } from "../../../graphql/types";
import { formatNumberDe } from "../../../utils/format";
import { CHART_COLORS } from "../chartTheme";
import { ChartCard } from "./ChartCard";
import "../chartSetup";

interface LineChartCardProps {
  title: string;
  points: AdminSeriesPoint[] | null;
  /** accent = orange registrations, warm = grey logins. */
  variant: "accent" | "warm";
  loading: boolean;
}

/**
 * A single dashboard line chart (FE-3): area-filled line with de-DE axis labels
 * and a styled hover tooltip. Registrations (accent) and Logins (warm) share it.
 */
export const LineChartCard: React.FC<LineChartCardProps> = ({ title, points, variant, loading }) => {
  const line = variant === "accent" ? CHART_COLORS.accent : CHART_COLORS.warm;
  const area = variant === "accent" ? CHART_COLORS.accentArea : CHART_COLORS.warmArea;

  const data = useMemo<ChartData<"line">>(
    () => ({
      labels: (points ?? []).map((p) => p.label),
      datasets: [
        {
          data: (points ?? []).map((p) => p.value),
          borderColor: line,
          backgroundColor: area,
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 2.5,
          pointHoverRadius: 5,
          pointBackgroundColor: line,
          pointBorderColor: line,
        },
      ],
    }),
    [points, line, area],
  );

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: CHART_COLORS.tooltipBg,
          borderColor: CHART_COLORS.tooltipBorder,
          borderWidth: 1,
          titleColor: CHART_COLORS.tick,
          bodyColor: CHART_COLORS.tooltipText,
          bodyFont: { weight: "bold", size: 14 },
          padding: 10,
          cornerRadius: 10,
          displayColors: false,
          callbacks: { label: (ctx) => formatNumberDe(ctx.parsed.y ?? 0) },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: CHART_COLORS.tick, font: { size: 11 }, maxTicksLimit: 6, autoSkip: true },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_COLORS.grid },
          border: { display: false },
          ticks: {
            color: CHART_COLORS.tick,
            font: { size: 11 },
            maxTicksLimit: 5,
            callback: (v) => formatNumberDe(Number(v)),
          },
        },
      },
    }),
    [],
  );

  return (
    <ChartCard title={title}>
      {!points && loading ? (
        <div className="lyra-skeleton chart-skeleton" />
      ) : (
        <Line data={data} options={options} />
      )}
    </ChartCard>
  );
};
